import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Card,
  Space,
  Typography,
  Tag,
  Spin,
  message,
  Button,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

const { Title, Text } = Typography;
const { Search } = Input;

interface ICD10Category {
  id: string;
  code: string;
  description: string;
  default_clinical_category: string;
}

interface CategoryStats {
  count: number;
}

export const ICD10CategoriesViewer: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<ICD10Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [stats, setStats] = useState<CategoryStats | null>(null);
  const [versionInfo, setVersionInfo] = useState<any>(null);

  // Check if we're viewing a specific version
  const versionId = searchParams.get('version');

  const fetchCategories = async (search = '', page = 1, pageSize = 50) => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      const searchParams = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
      });
      
      if (search) {
        searchParams.append('search', search);
      }

      // Use different endpoint based on whether we're viewing a specific version
      const endpoint = versionId 
        ? `${API_BASE_URL}/api/admin/data/icd10-categories/versions/${versionId}/data?${searchParams}`
        : `${API_BASE_URL}/api/admin/data/icd10-categories/categories?${searchParams}`;

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (search = '') => {
    try {
      const token = await getAccessTokenSilently();
      
      // Use different endpoint based on whether we're viewing a specific version
      let endpoint = versionId 
        ? `${API_BASE_URL}/api/admin/data/icd10-categories/versions/${versionId}/data/count`
        : `${API_BASE_URL}/api/admin/data/icd10-categories/categories/count`;

      // Add search parameter if provided (only for regular categories, not version-specific)
      if (search && !versionId) {
        const searchParams = new URLSearchParams({ search });
        endpoint += `?${searchParams}`;
      }

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
      setPagination(prev => ({ ...prev, total: data.count || 0 }));
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchVersionInfo = async () => {
    if (!versionId) return;
    
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/admin/data/icd10-categories/versions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }

      const versions = await response.json();
      const version = versions.find((v: any) => v.id === versionId);
      setVersionInfo(version);
    } catch (error) {
      console.error('Error fetching version info:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchStats(''); // Pass empty string for initial load (no search)
    fetchVersionInfo();
  }, [versionId]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchCategories(value, 1, pagination.pageSize);
    fetchStats(value);
  };

  const handleTableChange = (paginationConfig: any) => {
    const { current, pageSize } = paginationConfig;
    setPagination(prev => ({ ...prev, current, pageSize }));
    fetchCategories(searchText, current, pageSize);
  };

  const handleRefresh = () => {
    fetchCategories(searchText, pagination.current, pagination.pageSize);
    fetchStats(searchText); // Pass current search text to refresh count properly
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      minWidth: 80,
      render: (code: string) => (
        <Text strong style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {code}
        </Text>
      ),
      sorter: (a: ICD10Category, b: ICD10Category) => a.code.localeCompare(b.code),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      minWidth: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (description: string) => (
        <Tooltip placement="topLeft" title={description}>
          <Text style={{ fontSize: '12px' }}>{description}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Clinical Category',
      dataIndex: 'default_clinical_category',
      key: 'default_clinical_category',
      width: 180,
      minWidth: 150,
      render: (category: string) => (
        <Tag color="blue" style={{ 
          maxWidth: '160px', 
          wordWrap: 'break-word',
          fontSize: '11px',
          padding: '2px 6px'
        }}>
          {category}
        </Tag>
      ),
      filters: [
        ...Array.from(
          new Set(categories.map(cat => cat.default_clinical_category))
        ).map(category => ({
          text: category,
          value: category,
        })),
      ],
      onFilter: (value: any, record: ICD10Category) =>
        record.default_clinical_category === value,
    },
  ];

  return (
    <div style={{ 
      padding: '24px', 
      maxWidth: '100%', 
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Title level={2} style={{ 
              margin: 0, 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '20px'
            }}>
              <FileTextOutlined />
              ICD-10 Categories Data Viewer
              {versionInfo && (
                <Tag color={versionInfo.upload_status === 'active' ? 'green' : 'blue'} style={{ marginLeft: '8px' }}>
                  v{versionInfo.version_number} ({versionInfo.upload_status})
                </Tag>
              )}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {versionId && versionInfo 
                ? `Viewing version ${versionInfo.version_number} data (${versionInfo.upload_status}) - ${versionInfo.row_count?.toLocaleString()} records`
                : 'Browse and search through the current ICD-10 clinical categories data'
              }
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="small">
              Refresh
            </Button>
          </Space>
        </div>

        {stats && stats.count !== undefined && (
          <Card size="small" style={{ marginBottom: '16px' }}>
            <Space>
              <Tag color="green" style={{ margin: 0 }}>
                Total Records: {(stats.count || 0).toLocaleString()}
              </Tag>
              <Tag color="blue" style={{ margin: 0 }}>
                Showing: {Math.min(pagination.pageSize, categories.length)} of {pagination.total || 0}
              </Tag>
            </Space>
          </Card>
        )}

        <div style={{ marginBottom: '16px' }}>
          <Search
            placeholder="Search by code, description, or category..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={handleSearch}
            style={{ 
              maxWidth: '100%', 
              width: '100%'
            }}
          />
        </div>
      </div>

      <Card style={{ overflow: 'auto' }}>
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['25', '50', '100', '200'],
            responsive: true,
          }}
          onChange={handleTableChange}
          scroll={{ 
            x: 'max-content',
            scrollToFirstRowOnChange: true
          }}
          size="small"
        />
      </Card>
    </div>
  );
}; 